import { useState } from "react";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine } from "recharts";
import { MONTH_NAMES, roundCents, formatAUD } from "@/lib/format";

const c = (v, a = 1) => `hsl(var(--${v}) / ${a})`;
const num = (v) => Number(roundCents(v));

const Key = ({ color, label, dashed }) => (
  <span className="flex items-center gap-2 text-sm text-muted-foreground">
    <span className="inline-block w-4" style={{ borderTop: `3px ${dashed ? "dashed" : "solid"} ${color}` }} />{label}
  </span>
);

const Toggle = ({ on, onClick, label, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
    {label}
  </button>
);

// Simple view: money in vs money out per month, with the net as one line. Extras are opt-in.
export default function CashflowChart({ months, prevFy }) {
  const [showPrev, setShowPrev] = useState(false);
  const [showForecast, setShowForecast] = useState(false);
  const hasForecast = months.some((m) => num(m.forecast) !== 0);
  const data = months.map((m) => ({
    name: MONTH_NAMES[m.month - 1], "Money in": num(m.income), "Money out": num(m.expenditure),
    Net: num(m.income) === 0 && num(m.expenditure) === 0 ? null : num(m.net), Forecast: num(m.forecast), "Last year": num(m.prev_net),
  }));
  return (
    <div className="border border-border bg-card p-4 md:p-6" data-testid="cashflow-chart">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mb-4">
        <Key color={c("pos")} label="Money in" />
        <Key color={c("neg")} label="Money out" />
        <Key color={c("foreground")} label="Net (in − out)" />
        {showForecast && <Key color={c("warn")} label="Forecast" dashed />}
        {showPrev && <Key color={c("info")} label={`Net ${prevFy}`} dashed />}
        <div className="ml-auto flex gap-2">
          {hasForecast && <Toggle on={showForecast} onClick={() => setShowForecast((v) => !v)} label="Show forecast" testId="chart-toggle-forecast" />}
          <Toggle on={showPrev} onClick={() => setShowPrev((v) => !v)} label={`Compare ${prevFy}`} testId="chart-toggle-prev" />
        </div>
      </div>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ left: 0, right: 8 }} barGap={2}>
            <CartesianGrid stroke={c("border")} vertical={false} />
            <XAxis dataKey="name" stroke={c("muted-foreground")} fontSize={12} tickLine={false} axisLine={false} />
            <YAxis stroke={c("muted-foreground")} fontSize={11} tickLine={false} axisLine={false} width={48}
              tickFormatter={(v) => `${v < 0 ? "-" : ""}$${new Intl.NumberFormat("en-AU", { notation: "compact", maximumFractionDigits: 0 }).format(Math.abs(v))}`} />
            <ReferenceLine y={0} stroke={c("muted-foreground", 0.5)} />
            <Tooltip cursor={{ fill: c("accent", 0.5) }} formatter={(v) => formatAUD(String(v))}
              contentStyle={{ background: c("popover"), border: `1px solid ${c("border")}`, borderRadius: 12, color: c("foreground") }} />
            <Bar dataKey="Money in" fill={c("pos")} radius={[6, 6, 0, 0]} maxBarSize={22} />
            <Bar dataKey="Money out" fill={c("neg")} radius={[6, 6, 0, 0]} maxBarSize={22} />
            <Line dataKey="Net" stroke={c("foreground")} strokeWidth={2.5} dot={{ r: 3 }} />
            {showForecast && <Line dataKey="Forecast" stroke={c("warn")} strokeDasharray="6 4" strokeWidth={2} dot={false} />}
            {showPrev && <Line dataKey="Last year" name={`Net ${prevFy}`} stroke={c("info")} strokeDasharray="6 4" strokeWidth={2} dot={false} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
